import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { elementRefType } from '#/prop-types';
import { mergeRefs } from '#/shared/utils/react';
import * as Styled from './styled';

function SelectionColumn({
  items,
  selectedValue,
  onSelection,
  onFocus,
  scrollToSelection,
  columnRef,
  ariaLabel,
}) {
  const selectedItemRef = useRef();

  useEffect(() => {
    const selectedItem = selectedItemRef.current;

    if (scrollToSelection && selectedItem) {
      // using `scrollIntoView` here causes the whole page to move, this
      // approach ensures the child only scrolls in its direct parent
      selectedItem.parentNode.scrollTop = selectedItem.offsetTop;
    }
  }, [scrollToSelection]);

  const focusedItemRef = useRef();

  const onKeyDown = e => {
    const focusedItem = focusedItemRef.current;

    if (!focusedItem) {
      return;
    }

    const { previousSibling, nextSibling } = focusedItem;
    let nextFocusedItem = null;

    if (e.key === 'ArrowUp' && previousSibling) {
      nextFocusedItem = previousSibling;
    } else if (e.key === 'ArrowDown' && nextSibling) {
      nextFocusedItem = nextSibling;
    } else if (e.key === 'Enter') {
      onSelection(focusedItem.innerHTML);
    }

    if (nextFocusedItem) {
      // prevent default behavior of scrolling the container on arrow up/down
      // key press and centering an item when it's off-screen and gains focus
      e.preventDefault();
      nextFocusedItem.focus({ preventScroll: true });

      const container = nextFocusedItem.parentNode;
      const containerRect = container.getBoundingClientRect();
      const nextItemRect = nextFocusedItem.getBoundingClientRect();

      // if the element that has gained focus is off-screen scroll so its
      // fully visible and aligned to the top or bottom of the container;
      // whichever is closest
      if (containerRect.top > nextItemRect.top) {
        container.scrollTop = nextFocusedItem.offsetTop;
      } else if (containerRect.bottom < nextItemRect.bottom) {
        container.scrollTop =
          nextFocusedItem.offsetTop -
          containerRect.height +
          nextItemRect.height;
      }
    }
  };

  const onColumnFocus = e => {
    // the column itself is not allowed to gain focus, but if its `focus`
    // method is called forward the focus to its selected child element
    e.preventDefault();
    const selectedItem = selectedItemRef.current;

    if (selectedItem && selectedItem !== document.activeElement) {
      selectedItem.focus();
    }
  };

  const internalColumnRef = useRef();
  const [columnHasFocus, setColumnHasFocus] = useState(false);

  const onColumnBlur = () => {
    const columnElement = internalColumnRef?.current;

    if (columnElement && !columnElement.contains(document.activeElement)) {
      setColumnHasFocus(false);
    }
  };

  const onItemFocus = e => {
    focusedItemRef.current = e.target;
    onFocus();
    setColumnHasFocus(true);

    // prevent focus event from bubbling up to column element which
    // creates a loop to due its handler
    e.stopPropagation();
  };

  return (
    <Styled.SelectionColumn
      onKeyDown={onKeyDown}
      role="listbox"
      aria-label={ariaLabel}
      ref={mergeRefs(columnRef, internalColumnRef)}
      onFocus={onColumnFocus}
      onBlur={onColumnBlur}
      onMouseDown={e => {
        // prevent clicks on the element from firing its focus event to
        // stop the selected item from being scrolled into view
        if (e.currentTarget === e.target) e.preventDefault();
      }}
    >
      {items.map(v => (
        <Styled.SelectionColumnItem
          key={v}
          onClick={() => onSelection(v)}
          onFocus={onItemFocus}
          aria-selected={v === selectedValue ? true : undefined}
          ref={v === selectedValue ? selectedItemRef : undefined}
          // include the selected item in the tabbing flow when the column is
          // not focused and exclude other items, but allow them focus styling
          tabIndex={!columnHasFocus && v === selectedValue ? 0 : -1}
          role="option"
        >
          {v}
        </Styled.SelectionColumnItem>
      ))}
    </Styled.SelectionColumn>
  );
}

SelectionColumn.propTypes = {
  /** Array of values from which column items will be generated, in order */
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
  /** Value of item to be given selected styling and metadata */
  selectedValue: PropTypes.string.isRequired,
  /** Handler fired when selected value changes, is passed next selected value */
  onSelection: PropTypes.func.isRequired,
  /** Function invoked when any SelectionItem gains focus */
  onFocus: PropTypes.func,
  /** If true the selected item is scrolled to the top of the column */
  scrollToSelection: PropTypes.bool,
  /** `ref` object assigned to this component's column DOM element */
  columnRef: elementRefType,
  /** `aria-label` for top level element in this component */
  ariaLabel: PropTypes.string,
};

SelectionColumn.defaultProps = {
  onFocus: () => {},
  scrollToSelection: false,
  columnRef: undefined,
  ariaLabel: undefined,
};

export default SelectionColumn;
